import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
  useLayoutEffect
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { recallUpsellToken } from '../../../../../../../utils/upsellToken';
import {
  Box,
  Typography,
  Button,
  Dialog,
  IconButton,
  useMediaQuery,
  useTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { stripePromise } from '../../../../../../../utils/stripe';
import { Elements } from '@stripe/react-stripe-js';
import RaffleStripeCheckoutForm from './RaffleStripeCheckoutForm';
import SubscriptionCarousel from '../SubscriptionCarousel/SubscriptionCarousel';
import SubscriptionStackingExplainer from '../SubscriptionStackingExplainer/SubscriptionStackingExplainer';
import {
  META_PIXEL_ID,
  loadMetaPixel,
  trackInitiateCheckout as metaInitiateCheckout
} from '../../../../../../../utils/metaPixel';
import {
  loadTikTokPixel,
  trackInitiateCheckout as ttInitiateCheckout
} from '../../../../../../../utils/tiktokPixel';
import { YouTubeEmbed } from '../../../../../../../utils/youtubeEmbed';
import { track } from '../../../../../../../utils/track';
import GiveawayPrizeTimers from '../../../GiveawayPrizeTimers/GiveawayPrizeTimers';
import { GiveawayCountdownTimer } from '../RaffleHero/GiveawayCountdownTimer';
import {
  getFirstMonthDiscountedPrice,
  isDiscountEligibleSubscriptionPackage
} from '../../../../../../../utils/subscriptionDiscounts';
import { useNetwork } from '../../../../../../../utils/network';

// Stripe.js instance is the shared toggle-aware one imported above (utils/stripe).

export type PackItem = {
  id: string;
  title: string;
  price: string;
  entries: number;
  nfts: number;
  tokens?: number;
  description: string;
  dotColor: string;
  bonus?: {
    description: string;
    extraCards: number;
    extraEntries: number;
    price: number;
  };
};

type EntryFormValues = {
  fullName: string;
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  phone: string;
  countryIso2: string;
  countryName: string;
  stateIso2: string;
  stateName: string;
  phoneCountryCode: string;
  agreeToTerms: boolean;
  eligible: 'yes' | 'no' | '';
};

export type RafflePaymentPayload = {
  entry: EntryFormValues;
  selectedPack: PackItem; // one-off selected on raffle page
  standaloneItems: PackItem[];
  subscriptionItems: PackItem[];
  signupCodeBonusEntries?: number;
  signupCodeBonusSource?: 'giveaway_subs' | 'signup_code';
  /* 'promo-pack' is attribution only (features/PromoPack): it earns nothing. */
  signupSource?: 'giveaway' | 'giveaway-subs' | 'giveaway-offer' | 'promo-pack';

  // ✅ optional token when user verified existing account on raffle form
  raffleUnlockToken?: string | null;

  // ✅ NEW: passed from entry form
  hasActiveSubscription?: boolean;
};

type CheckoutMode = 'standalone' | 'subscription';

type ActiveGiveaway = {
  endDate: string;
  prizeAmount?: number | string | null;
};

const formatGiveawayPrize = (value?: number | string | null) => {
  const amount = Number(value);
  const safeAmount = Number.isFinite(amount) && amount > 0 ? amount : 10000;

  return `A$${safeAmount.toLocaleString('en-AU', {
    maximumFractionDigits: 0
  })}`;
};

const RafflePaymentPage = () => {
  const topRef = useRef<HTMLDivElement>(null);
  const TIKTOK_PIXEL_ID = process.env.REACT_APP_TIKTOK_PIXEL_ID || '';
  const { get } = useNetwork();
  const [jackpotGiveaway, setJackpotGiveaway] = useState<ActiveGiveaway | null>(
    null
  );

  useLayoutEffect(() => {
    // 1) Immediately jump to top
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;

    // 2) Focus a "top anchor" WITHOUT scrolling (prevents focus-jump to bottom later)
    //    Then force scroll again after paint in case something tries to restore
    const focusTop = () => {
      try {
        // preventScroll supported in modern browsers
        topRef.current?.focus({ preventScroll: true } as any);
      } catch {
        topRef.current?.focus();
      }
    };

    focusTop();
    requestAnimationFrame(() => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      focusTop();
    });

    const t1 = window.setTimeout(() => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      focusTop();
    }, 50);

    const t2 = window.setTimeout(() => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      focusTop();
    }, 250);

    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, []);

  const location = useLocation();
  const navigate = useNavigate();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const payload = useMemo<RafflePaymentPayload | null>(() => {
    const fromState = location.state as RafflePaymentPayload | undefined;
    if (fromState) return fromState;

    const saved = sessionStorage.getItem('rafflePaymentPayload');
    return saved ? (JSON.parse(saved) as RafflePaymentPayload) : null;
  }, [location.state]);

  useEffect(() => {
    if (!payload) navigate('..', { replace: true });
  }, [payload, navigate]);

  useEffect(() => {
    let cancelled = false;

    const loadJackpotCountdown = async () => {
      try {
        const data = await get('/giveaway/countdown', true);
        if (!cancelled && data?.endDate) {
          setJackpotGiveaway(data);
        }
      } catch (error) {
        console.error('Unable to load jackpot countdown:', error);
      }
    };

    loadJackpotCountdown();
    return () => {
      cancelled = true;
    };
  }, []);

  const [chosenSubId, setChosenSubId] = useState<string | null>(null);

  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [checkoutMode, setCheckoutMode] = useState<CheckoutMode>('standalone');

  const [bonusSelected, setBonusSelected] = useState(false);
  const [purchaseTracked, setPurchaseTracked] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    requestAnimationFrame(() => window.scrollTo(0, 0));
    const id = window.setTimeout(() => window.scrollTo(0, 0), 100);
    return () => window.clearTimeout(id);
  }, []);

  const entry = payload?.entry ?? null;
  const selectedPack = payload?.selectedPack ?? null;
  const subscriptionItems = useMemo(
    () => payload?.subscriptionItems ?? [],
    [payload?.subscriptionItems]
  );
  const displayedSubscriptionItems = useMemo(
    () =>
      [...subscriptionItems].sort(
        (a, b) => Number(a.price || 0) - Number(b.price || 0)
      ),
    [subscriptionItems]
  );

  const hasActiveSubscription = Boolean(payload?.hasActiveSubscription);
  const isAutoSubCheckout =
    hasActiveSubscription && checkoutMode === 'standalone';
  /* Whether to greet this buyer as a brand-new member.
   *
   * It used to read the raffle unlock token, which the entry form no longer mints —
   * so it was always false, and every returning member was shown "Welcome to
   * Incrypt" and told their login details had been emailed to them. No email is
   * sent: the server only mails a genuinely new registration.
   *
   * The honest answer comes back from the purchase itself (registrationCreated),
   * so it is threaded up from the checkout form and applied per payment below. */
  const isExistingVerifiedUser = Boolean(payload?.raffleUnlockToken);

  const selectedSubscriptionPack = useMemo(() => {
    if (!chosenSubId) return null;
    return subscriptionItems.find((p) => p.id === chosenSubId) ?? null;
  }, [chosenSubId, subscriptionItems]);

  const packForCheckout = useMemo(() => {
    if (checkoutMode === 'subscription') return selectedSubscriptionPack;
    return selectedPack;
  }, [checkoutMode, selectedSubscriptionPack, selectedPack]);

  const getSubscriptionStartingBonusEntries = (pack: PackItem | null) =>
    Math.max(0, Math.floor(Number(pack?.entries || 0) * 10));

  const checkoutPayload = useMemo(() => {
    if (!payload || checkoutMode !== 'subscription' || !packForCheckout) {
      return payload;
    }

    const signupCodeBonusEntries =
      getSubscriptionStartingBonusEntries(packForCheckout);

    if (signupCodeBonusEntries <= 0) return payload;

    return {
      ...payload,
      signupCodeBonusEntries,
      signupCodeBonusSource: 'signup_code' as const
    };
  }, [checkoutMode, packForCheckout, payload]);

  const resetModalState = () => {
    setBonusSelected(false);
  };

  const trackCheckoutOpen = (opts: {
    mode: CheckoutMode;
    pack: PackItem | null;
    bonusSelected?: boolean;
  }) => {
    const valueAud = opts.pack
      ? getDisplayedCheckoutPrice(
          opts.pack,
          opts.mode,
          30,
          Boolean(opts.bonusSelected)
        )
      : 0;

    if (!Number.isFinite(valueAud) || valueAud <= 0) return;

    const contentId = String(opts.pack?.id);
    const contentName = opts.pack?.title ?? 'Unknown Pack';

    if (META_PIXEL_ID) {
      loadMetaPixel(META_PIXEL_ID);
      metaInitiateCheckout({
        contentId,
        contentName,
        value: valueAud,
        currency: 'AUD'
      });
    }

    if (TIKTOK_PIXEL_ID) {
      loadTikTokPixel(TIKTOK_PIXEL_ID);
      ttInitiateCheckout({
        contentId,
        contentName,
        value: valueAud,
        currency: 'AUD'
      });
    }
  };

  const openCheckout = (mode: CheckoutMode) => {
    if (!payload) return;

    const pack =
      mode === 'subscription' ? selectedSubscriptionPack : selectedPack;

    if (mode === 'subscription' && !pack) return;
    if (mode === 'standalone' && !pack) return;

    trackCheckoutOpen({
      mode,
      pack,
      bonusSelected: false
    });

    setCheckoutMode(mode);
    resetModalState();
    setCheckoutOpen(true);
  };

  const openSubscriptionCheckout = (id: string) => {
    if (!payload) return;

    const pack = subscriptionItems.find((item) => item.id === id) ?? null;
    if (!pack) return;

    setChosenSubId(id);
    setCheckoutMode('subscription');
    resetModalState();
    trackCheckoutOpen({
      mode: 'subscription',
      pack,
      bonusSelected: false
    });
    setCheckoutOpen(true);
  };

  const closeCheckout = () => {
    setCheckoutOpen(false);
    resetModalState();
  };

  const computeTotals = (
    pack: PackItem,
    bonusSelected: boolean,
    mode: CheckoutMode
  ) => {
    const baseEntries = Number(pack.entries ?? 0);
    const baseNfts = Number(pack.nfts ?? 0);

    const bonusEntries = bonusSelected
      ? Number(pack.bonus?.extraEntries ?? 0)
      : 0;
    const subscriptionStartingBonusEntries =
      mode === 'subscription' ? getSubscriptionStartingBonusEntries(pack) : 0;
    const bonusNfts = bonusSelected ? Number(pack.bonus?.extraCards ?? 0) : 0;

    const amountPaidAud = getDisplayedCheckoutPrice(
      pack,
      mode,
      30,
      bonusSelected
    );

    return {
      totalEntries:
        baseEntries + bonusEntries + subscriptionStartingBonusEntries,
      nftRewards: baseNfts + bonusNfts,
      amountPaidAud,
      bonusSelected
    };
  };

  const saveConfirmationDraft = (opts?: {
    bonusSelected?: boolean;
    mode?: CheckoutMode;
    paymentIntentId?: string;
    subscriptionId?: string;
    accountCreated?: boolean;
  }) => {
    const pack = packForCheckout!;
    const bonusSel = Boolean(opts?.bonusSelected);
    const mode = opts?.mode ?? checkoutMode;

    const totals = computeTotals(pack, bonusSel, mode);

    const confirmationState = {
      entry: {
        firstName: payload?.entry?.firstName ?? '',
        lastName: payload?.entry?.lastName ?? '',
        username: payload?.entry?.username ?? '',
        email: payload?.entry?.email ?? ''
      },
      selectedPack: pack,
      totalEntries: totals.totalEntries,
      nftRewards: totals.nftRewards,
      amountPaidAud: totals.amountPaidAud,
      checkoutMode: mode,
      incPerMonth:
        mode === 'subscription' ? Number(pack.tokens ?? 0) : undefined,
      /* opts.accountCreated is what the purchase actually did; fall back to the
       * token check for any path that has not reported one. */
      isExistingVerifiedUser:
        opts?.accountCreated !== undefined
          ? !opts.accountCreated
          : isExistingVerifiedUser,
      memberPortalPath: '/login',
      bonusSelected: totals.bonusSelected,

      // ✅ add these
      paymentIntentId: opts?.paymentIntentId,
      subscriptionId: opts?.subscriptionId
    };

    sessionStorage.setItem(
      'giveawayConfirmationState',
      JSON.stringify(confirmationState)
    );
  };

  const getConfirmationPath = () => {
    // if it's subscription checkout
    const isSub = checkoutMode === 'subscription';
    return isSub
      ? '/giveaway/confirmation-sub'
      : '/giveaway/confirmation-one-off';
  };

  const goToConfirmation = () => {
    sessionStorage.removeItem('giveawayUpsellPaymentIntentId');

    try {
      const saved = sessionStorage.getItem('giveawayConfirmationState');
      const upsellApplied = sessionStorage.getItem('giveawayUpsellApplied');

      if (saved) {
        const conf = JSON.parse(saved);

        if (upsellApplied) {
          const up = JSON.parse(upsellApplied);

          conf.totalEntries =
            Number(conf.totalEntries ?? 0) + Number(up.extraEntries ?? 0);

          conf.nftRewards =
            Number(conf.nftRewards ?? 0) + Number(up.extraNfts ?? 0);

          conf.amountPaidAud =
            Number(conf.amountPaidAud ?? 0) + Number(up.price ?? 0);

          conf.upsellApplied = up;

          sessionStorage.removeItem('giveawayUpsellApplied');
        }

        sessionStorage.setItem(
          'giveawayConfirmationState',
          JSON.stringify(conf)
        );

        // ✅ if we have conf.checkoutMode, use that (more reliable)
        const confIsSub = conf.checkoutMode === 'subscription';
        const path = confIsSub
          ? '/giveaway/confirmation-sub'
          : '/giveaway/confirmation-one-off';

        navigate(path, { replace: true });
        return;
      }
    } catch {
      // ignore and fall through
    }

    // fallback
    navigate(getConfirmationPath(), { replace: true });
  };

  const handlePaid = async (args: {
    mode: CheckoutMode;
    paymentIntentId?: string;
    bonusSelected?: boolean;
    accountCreated?: boolean;
  }) => {
    // First-party conversion event — giveaway entry paid (all modes route here).
    track('giveaway.enter', {
      mode: args.mode,
      pack: packForCheckout?.title,
      valueAud: Number(totalPrice ?? 0)
    });
    if (args.mode !== 'standalone' || !args.paymentIntentId) {
      const valueAud = Number(totalPrice ?? 0);
      const packName = packForCheckout?.title ?? 'Unknown Pack';

      saveConfirmationDraft({
        mode: 'subscription',
        bonusSelected: false,
        accountCreated: args.accountCreated
      });
      goToConfirmation();
      return;
    }

    const valueAud = Number(totalPrice ?? 0);
    const packName = packForCheckout?.title ?? 'Unknown Pack';

    const packHasBonus = Boolean(packForCheckout?.bonus);
    const bonusSelected = Boolean(args.bonusSelected);

    setBonusSelected(bonusSelected);

    saveConfirmationDraft({
      mode: 'standalone',
      bonusSelected,
      paymentIntentId: args.paymentIntentId,
      accountCreated: args.accountCreated
    });

    /* Only offer what we can actually charge. The follow-up charge is authorised by
     * the token the checkout stored for this payment intent; without it the offer
     * would look normal and fail on the click (see utils/upsellToken). */
    const shouldShowUpsell =
      (!packHasBonus || (packHasBonus && bonusSelected)) &&
      Boolean(recallUpsellToken(args.paymentIntentId));

    if (!shouldShowUpsell) {
      saveConfirmationDraft({
        bonusSelected,
        paymentIntentId: args.paymentIntentId,
        accountCreated: args.accountCreated
      });

      goToConfirmation();
      return;
    }

    // 🔍 Ask backend what upsell exists
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/checkout/upsell-details/${args.paymentIntentId}`,
        { credentials: 'include' }
      );

      if (!res.ok) {
        // 404 = no upsell (expected sometimes)
        goToConfirmation();
        return;
      }

      saveConfirmationDraft({ bonusSelected, accountCreated: args.accountCreated });

      // save for refresh fallback
      sessionStorage.setItem(
        'giveawayUpsellPaymentIntentId',
        args.paymentIntentId
      );

      // close checkout dialog so we don’t have a weird overlay while routing
      setCheckoutOpen(false);

      // go to upsell page
      navigate('/giveaway/upsell', {
        state: { paymentIntentId: args.paymentIntentId }
      });
      return;
    } catch {
      goToConfirmation();
    }
  };

  // ✅ If user already has an active subscription, skip the offer screen and
  // auto-open the standalone checkout immediately.
  const [autoOpened, setAutoOpened] = useState(false);
  useEffect(() => {
    if (!payload) return;
    if (!hasActiveSubscription) return;
    if (autoOpened) return;

    setAutoOpened(true);

    if (!selectedPack) return;

    openCheckout('standalone');
  }, [payload, hasActiveSubscription, autoOpened, selectedPack]);

  const getDisplayedCheckoutPrice = (
    pack: PackItem,
    mode: CheckoutMode,
    discountPercent: number,
    bonusSelected: boolean
  ) => {
    const basePrice = Number(pack.price ?? 0);
    const bonusPrice = bonusSelected ? Number(pack.bonus?.price ?? 0) : 0;

    const isDiscountEligibleSubscription =
      mode === 'subscription' && isDiscountEligibleSubscriptionPackage(pack.id);

    const discountedBasePrice = isDiscountEligibleSubscription
      ? getFirstMonthDiscountedPrice(basePrice, discountPercent)
      : basePrice;

    return discountedBasePrice + bonusPrice;
  };

  const totalPrice = packForCheckout
    ? getDisplayedCheckoutPrice(
        packForCheckout,
        checkoutMode,
        30,
        bonusSelected
      )
    : 0;

  if (!payload) return null;

  // ✅ When subscribed: don't show subscription carousel page at all
  const showSubscriptionOffer = !hasActiveSubscription;

  const isSubscription = checkoutMode === 'subscription';

  const amountCents = Math.round(
    (packForCheckout
      ? getDisplayedCheckoutPrice(
          packForCheckout,
          checkoutMode,
          30,
          bonusSelected
        )
      : 0) * 100
  );

  const elementsKey = `${checkoutMode}:${amountCents}:${isSubscription ? 'off_session' : 'on_session'}`;

  return (
    <Box sx={{ px: { xs: 2, md: 4, lg: 8 }, py: 6, backgroundColor: 'white' }}>
      <Box
        ref={topRef}
        tabIndex={-1}
        aria-hidden="true"
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: 1,
          height: 1,
          outline: 'none'
        }}
      />

      {showSubscriptionOffer ? (
        <>
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Typography
              sx={{
                // mt: 2.5,
                fontSize: { xs: 42, md: 64 },
                fontWeight: 950,
                color: 'black',
                letterSpacing: '-0.03em'
              }}
            >
              Wait! Upgrade to Subscription?
            </Typography>

            <Typography
              sx={{
                // mt: 1,
                fontSize: { xs: 18, md: 22 },
                fontWeight: 700,
                color: 'black'
              }}
            >
              Get entered into every weekly draw with a monthly subscription
              package
            </Typography>
          </Box>

          <YouTubeEmbed
            url="https://www.youtube.com/watch?v=F-5XjBKv7Lg"
            title="Incrypt Subscription Packs"
          />

          {jackpotGiveaway?.endDate && (
            <Box
              sx={{
                my: { xs: 4, md: 6 },
                mx: 'auto',
                maxWidth: 1320,
                px: { xs: 2.2, md: 5 },
                py: { xs: 3, md: 5.5 },
                borderRadius: { xs: 4, md: 5 },
                border: '2px solid rgba(65,200,196,0.34)',
                background:
                  'radial-gradient(circle at top, rgba(65,200,196,0.20), transparent 34%), linear-gradient(135deg, #020617 0%, #07111f 55%, #082f49 100%)',
                boxShadow:
                  '0 26px 70px rgba(2,6,23,0.30), 0 0 34px rgba(65,200,196,0.14)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                overflow: 'hidden'
              }}
            >
              <Typography
                sx={{
                  mb: { xs: 2.4, md: 4 },
                  color: '#ffffff',
                  fontSize: { xs: 32, sm: 44, md: 70 },
                  lineHeight: 1,
                  fontWeight: 950,
                  textAlign: 'center',
                  letterSpacing: '0.04em'
                }}
              >
                {formatGiveawayPrize(jackpotGiveaway.prizeAmount)} GIVEAWAY LIVE
                IN:
              </Typography>
              <GiveawayCountdownTimer
                nextGiveaway={new Date(jackpotGiveaway.endDate)}
                isDense={isMobile}
                isJumbo={!isMobile}
              />
            </Box>
          )}

          <Box sx={{ mt: { xs: 3, md: 4 }, mb: { xs: 3, md: 4 } }}>
            <GiveawayPrizeTimers compact />
          </Box>

          <SubscriptionStackingExplainer items={displayedSubscriptionItems} />

          <SubscriptionCarousel
            items={displayedSubscriptionItems}
            selectedId={chosenSubId ?? ''}
            onSelect={openSubscriptionCheckout}
            lastPackDiscountPercent={30}
          />

          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Typography
              sx={{
                mt: 2.2,
                fontSize: 18,
                fontWeight: 800,
                color: 'black'
              }}
            >
              Current selection: One-Off{' '}
              <span style={{ color: 'black' }}>
                {selectedPack?.title ?? ''}
              </span>
            </Typography>
          </Box>

          <Box
            sx={{
              mt: 5,
              justifyContent: 'center',
              display: 'flex'
            }}
          >
            <Button
              onClick={() => openCheckout('standalone')}
              variant="outlined"
              sx={{
                height: 52,
                px: 4,
                borderRadius: 999,
                fontWeight: 900,
                borderColor: 'black',
                color: 'black'
              }}
            >
              No Thanks, I&apos;ll Keep My One-Off Entry
            </Button>
          </Box>
        </>
      ) : (
        // ✅ Optional: if subscribed, you can show a tiny “redirecting” header
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography
            sx={{
              fontSize: { xs: 28, md: 34 },
              fontWeight: 950,
              color: 'black'
            }}
          >
            Taking you to checkout…
          </Typography>
          <Typography
            sx={{
              mt: 1,
              fontWeight: 700,
              color: 'black'
            }}
          >
            You already have an active subscription, so we skipped the upgrade
            offer.
          </Typography>

          <Box sx={{ mt: 2 }}>
            <Button
              onClick={() => openCheckout('standalone')}
              sx={{
                height: 48,
                px: 4,
                borderRadius: 999,
                fontWeight: 950,
                color: 'rgba(0,0,0,0.85)',
                background: 'green',
                '&:hover': {
                  background: 'darkgreen'
                }
              }}
            >
              Open Checkout
            </Button>
          </Box>
        </Box>
      )}

      <Dialog
        open={checkoutOpen}
        fullScreen={isMobile}
        maxWidth={false}
        disableEscapeKeyDown={isAutoSubCheckout}
        onClose={(event, reason) => {
          if (isAutoSubCheckout) return;
          if (reason === 'backdropClick') return;
          if (reason === 'escapeKeyDown') return;
          closeCheckout();
        }}
        PaperProps={{
          sx: {
            width: isMobile ? '100%' : '50vw',
            maxWidth: isMobile ? '100%' : 760,
            borderRadius: isMobile ? 0 : 4,
            background: '#ffffff',
            border: '1px solid rgba(18, 31, 38, 0.10)',
            boxShadow: '0 24px 64px rgba(18, 31, 38, 0.14)'
          }
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 2,
            py: 1.5,
            borderBottom: '1px solid rgba(18,31,38,0.08)'
          }}
        >
          <Typography
            sx={{
              fontWeight: 950,
              color: '#111827',
              fontSize: 16
            }}
          >
            Checkout
          </Typography>

          <Box sx={{ ml: 'auto' }}>
            <IconButton
              onClick={closeCheckout}
              sx={{ color: 'rgba(17,24,39,0.68)' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          <Elements
            key={elementsKey}
            stripe={stripePromise}
            options={{
              mode: isSubscription ? 'subscription' : 'payment',
              currency: 'aud',
              paymentMethodTypes: ['card'],
              setupFutureUsage: isSubscription ? 'off_session' : 'on_session',
              amount: amountCents,
              appearance: {
                theme: 'stripe',
                variables: {
                  fontSizeBase: '16px'
                },
                rules: { '.Label': { color: '#111827' } }
              }
            }}
          >
            <RaffleStripeCheckoutForm
              payload={checkoutPayload ?? undefined}
              mode={checkoutMode}
              pack={packForCheckout!}
              onPaid={handlePaid}
              subDiscount={30}
              discountAllSubscriptionPacks={
                checkoutMode === 'subscription' &&
                isDiscountEligibleSubscriptionPackage(packForCheckout!.id)
              }
            />
          </Elements>
        </Box>
      </Dialog>
    </Box>
  );
};

export default RafflePaymentPage;
